import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { IMenusProperties } from '@legacy/apis/types/menusAPI';
import { formatUrl, concatQueries } from '@legacy/utils/url';
import { useHelpURLInfo } from '@legacy/apis/usersAPI/meAPI';
import { useGetHelpInfo } from '@legacy/apis/indexAPI';
import { useGetCurrentLanguage } from '@legacy/apis/userSettingsAPI';
import { helpUrl } from './constants';

// --- Types and initial state (moved from reducer) ---
type TPageIdentifier = string;
type TPageId = string;
type TPageUrl = string;

interface LastPageProps {
  pageUrl: TPageUrl;
  pageSettingUrl: TPageUrl;
}

interface CurrentPageProps {
  pageId: TPageId;
  pageIdentifier: TPageIdentifier;
  pageTitle: string;
}

interface PageSettingProps {
  pageSettingUrl: TPageUrl;
  pageId: TPageId;
}

export interface PageProps {
  lastPage: LastPageProps;
  currentPage: CurrentPageProps;
  currentModalPage: CurrentPageProps;
  pageSettings: Record<TPageIdentifier, PageSettingProps>;
}

const initPageStore: PageProps = {
  lastPage: {
    pageUrl: '/',
    pageSettingUrl: '/page-settings/no-page-settings-found',
  },
  currentPage: { pageIdentifier: '', pageId: '', pageTitle: '' },
  pageSettings: {},
  currentModalPage: { pageIdentifier: '', pageId: '', pageTitle: '' },
};

export const pageSettingMapping = (
  menuItems: IMenusProperties[],
): Pick<PageProps, 'pageSettings'> => {
  const defaultValue: Pick<PageProps, 'pageSettings'> = {
    pageSettings: {},
  };
  menuItems.forEach((menuItem) => {
    if (menuItem.pageIdentifier && menuItem.pageUrl && menuItem.pageId) {
      defaultValue.pageSettings[menuItem.pageIdentifier] = {
        pageSettingUrl: formatUrl(menuItem.pageUrl),
        pageId: menuItem.pageId,
      };
    }
    const { subMenus = [] } = menuItem;
    subMenus?.forEach(({ pageIdentifier, pageUrl, pageId }) => {
      defaultValue.pageSettings[pageIdentifier] = {
        pageSettingUrl: formatUrl(pageUrl),
        pageId,
      };
    });
  });
  return defaultValue;
};

// --- Zustand Store ---
interface PageStore extends PageProps {
  setCurrentPage: (page: CurrentPageProps) => void;
  setCurrentModalPage: (page: CurrentPageProps) => void;
  setLastVisitedPage: (pagePath: string, pageIdentifier: string) => void;
  setPageSettings: (menuItems: IMenusProperties[]) => void;
}

export const usePageStore = create<PageStore>()(
  devtools((set, get) => ({
    ...initPageStore,
    setCurrentPage: (page) =>
      set((state) => ({
        ...state,
        currentPage: {
          pageTitle: page.pageTitle || '',
          pageIdentifier: page.pageIdentifier || '',
          pageId: page.pageId,
        },
      })),
    setCurrentModalPage: (page) =>
      set((state) => ({
        ...state,
        currentModalPage: {
          ...page,
        },
      })),
    setLastVisitedPage: (pagePath, pageIdentifier) =>
      set((state) => ({
        ...state,
        lastPage: {
          pageUrl: pagePath || '/',
          pageSettingUrl:
            state.pageSettings?.[pageIdentifier]?.pageSettingUrl ||
            '/page-settings/no-page-settings-found',
        },
      })),
    setPageSettings: (menuItems) =>
      set((state) => {
        const { pageSettings } = pageSettingMapping(menuItems);
        return menuItems.length > 0
          ? {
              ...state,
              pageSettings,
              lastPage: {
                ...state.lastPage,
                pageSettingUrl:
                  state.lastPage.pageSettingUrl === ''
                    ? '/page-settings/no-page-settings-found'
                    : state.lastPage.pageSettingUrl,
              },
            }
          : state;
      }),
  })),
);

// Selectors (replace custom hooks)
export const useGetPageSetting = () =>
  usePageStore((s) => s.lastPage.pageSettingUrl);
export const useGetBackToTimeLogPage = () =>
  usePageStore((s) => s.lastPage.pageUrl);
export const useGetCurrentPageId = () =>
  usePageStore((s) => s.currentPage.pageId);
export const useGetCurrentPageIdentifier = () =>
  usePageStore((s) => s.currentPage.pageIdentifier);
export const useGetCurrentPageTitle = () =>
  usePageStore((s) => s.currentPage.pageTitle);

// [OBSOLETED] Due to moving away from Umbraco.
// For help page URL, keep as a hook since it uses other hooks
export const useGetCurrentHelpPageUrl = (): string | undefined => {
  const pageId = usePageStore((s) => s.currentPage.pageId);
  const pageModalId = usePageStore((s) => s.currentModalPage.pageId);
  const {
    helpInfo: { email, initials },
    isSuccess: isHelpURLInfoSuccess,
  } = useHelpURLInfo();
  const {
    helpInfo: { version, shortName, edition },
    isSuccess: isGetHelpInfoSuccess,
  } = useGetHelpInfo();
  const { longStringLanguage } = useGetCurrentLanguage();

  return isHelpURLInfoSuccess && isGetHelpInfoSuccess
    ? `${helpUrl}${concatQueries([
        { param: 'pageId', value: pageModalId || pageId },
        { param: 'shortName', value: shortName },
        { param: 'initials', value: initials },
        { param: 'email', value: email },
        { param: 'language', value: longStringLanguage },
        { param: 'version', value: version },
        { param: 'edition', value: edition },
      ])}`
    : `${helpUrl}${concatQueries([
        { param: 'pageId', value: pageModalId || pageId },
        { param: 'language', value: longStringLanguage },
      ])}`;
};
